import { useMemo } from 'react';
import type { StateResponse } from '../lib/api.js';
import StatusGlow from './ui/StatusGlow.js';

// TickerBanner v0.2.0 — scrolling marquee at the top of the public site.
// Operator-curated via /admin/tickers; one ticker is active at a time
// (single-active enforced server-side). HTML is pre-rendered server-side
// via the strict allowlist in @secdefcon/shared, so dangerouslySetInnerHTML
// is the correct call and the content must NOT be re-escaped.
//
// The marquee technique: a flex track holds two identical copies of the
// rendered content side-by-side, then animates translateX from 0 to -50%
// of the track. Because the second copy is exactly the first copy, the
// loop point is invisible. We render once then duplicate via React rather
// than CSS pseudo-elements so dangerouslySetInnerHTML still works.
//
// Duration scales with content length — short messages would crawl across
// the screen for 45s otherwise. The chosen ratio (~0.18s per character,
// minimum 18s) keeps reading speed roughly constant regardless of length.
const SEV_BORDER: Record<string, string> = {
  info: 'border-accent',
  warn: 'border-warn',
  critical: 'border-magenta',
};
const SEV_LABEL: Record<string, string> = {
  info: 'text-accent',
  warn: 'text-warn',
  critical: 'text-magenta',
};
const SEV_GLOW: Record<string, 1 | 2 | 3> = {
  info: 1,
  warn: 2,
  critical: 3,
};
const SEV_BANNER_BORDER: Record<string, string> = {
  info: 'border-accent/35',
  warn: 'border-warn/60',
  critical: 'border-magenta/65',
};

export default function TickerBanner({ ticker }: { ticker: StateResponse['ticker'] }) {
  const durationS = useMemo(() => {
    if (!ticker) return 45;
    // Approximate character count by stripping HTML tags. The animation
    // travels twice the visible width (since the track is 2× content +
    // duplicates). We aim for ~80–100 visible chars per 10s of motion.
    const stripped = ticker.html_rendered.replace(/<[^>]+>/g, '');
    const charCount = Math.max(40, stripped.length);
    return Math.max(18, Math.min(180, Math.round(charCount * 0.18)));
  }, [ticker]);

  if (!ticker) return null;
  const borderCls = SEV_BORDER[ticker.severity] ?? SEV_BORDER.info;
  const labelCls = SEV_LABEL[ticker.severity] ?? SEV_LABEL.info;
  const bannerBorder = SEV_BANNER_BORDER[ticker.severity] ?? SEV_BANNER_BORDER.info;
  const glowTone = SEV_GLOW[ticker.severity] ?? 1;
  return (
    <div
      className={`relative overflow-hidden border-y ${bannerBorder} ${borderCls} bg-surface`}
      role="marquee"
      aria-live="polite"
      aria-label={`Ticker: ${ticker.severity}`}
    >
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center bg-surface px-3 border-r border-border">
        <StatusGlow severity={glowTone} className="px-2 py-0.5 text-[10px]">
          <span className={`${labelCls} tracking-widest uppercase`}>{ticker.severity}</span>
        </StatusGlow>
      </div>
      <div
        className="flex w-max animate-ticker-slide pl-24 py-2"
        style={{ animationDuration: `${durationS}s` }}
      >
        {/* Two duplicate copies form the seamless loop. aria-hidden on the
            duplicate keeps screen readers from announcing the message twice.
            HTML is server-side rendered via @secdefcon/shared's strict
            allowlist (links / bold / code only) — re-escaping would
            double-escape sanitized output. */}
        <span
          className="px-8 whitespace-nowrap text-text"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: see span-level comment above
          dangerouslySetInnerHTML={{ __html: ticker.html_rendered }}
        />
        <span
          aria-hidden="true"
          className="px-8 whitespace-nowrap text-text"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: duplicated copy for seamless marquee loop, same trusted source as above
          dangerouslySetInnerHTML={{ __html: ticker.html_rendered }}
        />
      </div>
    </div>
  );
}
